
import { UseFormRegister, UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { TagInput } from './fields/TagInput';
import type { FormData } from './types';

interface ServiceCapabilitiesProps {
  register: UseFormRegister<FormData>;
  setValue: UseFormSetValue<FormData>;
  getValues: UseFormGetValues<FormData>;
}

export const ServiceCapabilities = ({ register, setValue, getValues }: ServiceCapabilitiesProps) => {
  const handleMobileAppToggle = (checked: boolean) => {
    setValue('mobile_app', checked);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Service Capabilities</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Information about the PEO's technology, services, and partners.
      </p>
      
      <div className="grid gap-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Mobile App</Label>
            <p className="text-sm text-muted-foreground">
              Does the PEO offer a mobile application?
            </p>
          </div>
          <Switch 
            checked={!!getValues('mobile_app')}
            onCheckedChange={handleMobileAppToggle}
          />
        </div>

        <TagInput
          label="Payroll Platform"
          placeholder="e.g. ADP, Paychex, Gusto"
          description="Primary payroll platform used by the PEO"
          fieldName="payroll_platform"
          setValue={setValue}
          getValues={getValues}
        />

        <TagInput
          label="Medical Carriers"
          placeholder="e.g. Aetna, Blue Cross Blue Shield, Cigna"
          description="Medical insurance carriers available through the PEO"
          fieldName="medical_carriers"
          setValue={setValue}
          getValues={getValues}
        />

        <TagInput
          label="Ancillary Carriers"
          placeholder="e.g. Principal, MetLife, Aflac, Guardian"
          description="Ancillary insurance carriers available through the PEO"
          fieldName="ancillary_carriers"
          setValue={setValue}
          getValues={getValues}
        />

        <TagInput
          label="Workers Comp Carriers"
          placeholder="e.g. AmTrust, Berkshire Hathaway, Liberty Mutual"
          description="Workers' compensation carriers available through the PEO"
          fieldName="workers_comp_carriers"
          setValue={setValue}
          getValues={getValues}
        />

        <TagInput
          label="401k Vendor"
          placeholder="e.g. Fidelity, Vanguard, John Hancock"
          description="401k vendors available through the PEO"
          fieldName="k401_vendor"
          setValue={setValue}
          getValues={getValues}
        />

        <TagInput
          label="Renewal Date"
          placeholder="MM-DD"
          description="Enter renewal dates in MM-DD format (e.g., 01-15, 06-30)"
          fieldName="renewal_date"
          setValue={setValue}
          getValues={getValues}
          validation={(value) => {
            // Validate MM-DD format
            const dateRegex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
            if (!dateRegex.test(value)) {
              return 'Please enter date in MM-DD format (e.g., 01-15)';
            }

            const [month, day] = value.split('-');
            const monthNum = parseInt(month);
            const dayNum = parseInt(day);
            
            if (monthNum < 1 || monthNum > 12) {
              return 'Month must be between 01 and 12';
            }
            
            if (dayNum < 1 || dayNum > 31) {
              return 'Day must be between 01 and 31';
            }

            return null; // Valid
          }}
        />
      </div>
    </div>
  );
};
